import React, { useEffect, useState, useRef } from "react";
import { 
  Typography, LinearProgress, Box, Button, Stack, Paper, Container, 
  FormControlLabel, Checkbox,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell 
} from "@mui/material";
import { OCR_ENGINE_LABELS } from "../ocr/engineTypes";
import { arrayToCsv, downloadBlob } from "../utils/csvutils";
import { invertImageFilter, thresholdFilter } from "../utils/filters";
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const { createWorker, createScheduler } = require("tesseract.js");

function ProcessVideo({ videoFile, startFrame, endFrame, rectangles, updateInterval, ocrEngineType }) {
    const [progress, setProgress] = useState(0);
    const [results, setResults] = useState([]);
    const [videoSrc, setVideoSrc] = useState("");
    const [processingComplete, setProcessingComplete] = useState(false);
    const [trolled, setTrolled] = useState(false);
    const [blobData, setBlobData] = useState(null);
    const [hasStarted, setHasStarted] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    // Store timestamps and final results for the graph after processing
    const [timestamps, setTimestamps] = useState([]);
    const [chartData, setChartData] = useState([]); // numeric values per region [regionIndex][frameIndex]

    const [runtime, setRuntime] = useState(0); // processing time in seconds

    // Graph region selection (max 2 regions)
    const [selectedRegionsForGraph, setSelectedRegionsForGraph] = useState([]); // array of region indices

    // Hover state for showing historical previews
    const [hoveredTimestampIndex, setHoveredTimestampIndex] = useState(null);

    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    const totalFrames = (startFrame != null && endFrame != null && updateInterval)
        ? Math.max(1, Math.round((endFrame - startFrame) / updateInterval))
        : 0;

    // Create (and clean up) the object URL for the video file.
    // The actual seeking and metadata handling is done inside processVideoInternal
    // after the elements are guaranteed to be in the DOM.
    useEffect(() => {
        if (!videoFile) {
            setVideoSrc("");
            return;
        }
        const src = URL.createObjectURL(videoFile);
        setVideoSrc(src);

        return () => {
            URL.revokeObjectURL(src);
        };
    }, [videoFile]);

    const startExtraction = () => {
        setHasStarted(true);
        setIsProcessing(true);
        // Actual processing will be triggered by the useEffect below once the DOM updates
    };

    // When hasStarted becomes true, the new video + preview canvases are now in the DOM.
    // Wait one tick for React to paint, then start the real work.
    useEffect(() => {
        if (hasStarted && isProcessing) {
            // Wait for React to paint the preview canvases before starting
            const timer = setTimeout(() => {
                processVideoInternal();
            }, 120); // Increased delay for reliability with dynamic canvases
            return () => clearTimeout(timer);
        }
    }, [hasStarted, isProcessing]);

    const processVideoInternal = async () => {
        const video = document.getElementById("video");
        console.log("Processing video...");
        if (!video) return;

        // Wait for the video element (which was just mounted) to have metadata loaded
        if (!video.videoWidth || !video.videoHeight) {
            await new Promise((resolve) => {
                const onMeta = () => {
                    video.removeEventListener("loadedmetadata", onMeta);
                    resolve();
                };
                video.addEventListener("loadedmetadata", onMeta, { once: true });
                if (video.videoWidth) resolve();
            });
        }

        // Size the hidden full-size canvas to match the video resolution
        const hiddenCanvas = document.getElementById("canvas");
        if (hiddenCanvas) {
            hiddenCanvas.width = video.videoWidth;
            hiddenCanvas.height = video.videoHeight;
        }

        // Make sure we're at the starting position
        video.currentTime = startFrame;
        await new Promise((resolve) => {
            const onSeek = () => {
                video.removeEventListener("seeked", onSeek);
                resolve();
            };
            video.addEventListener("seeked", onSeek, { once: true });
        });
        const scheduler = createScheduler();
        let workers = [];
        let canvasList = [];
        let data = []; // raw OCR strings for CSV
        const timestamps = [];
        let numericData = Array.from({ length: rectangles.length }, () => []); // numeric values for charting
        for (let i = 0; i < rectangles.length; i++) {
            let worker = await createWorker("eng");
            workers.push(worker);
            scheduler.addWorker(worker);
            canvasList.push(document.getElementById(`preview${i}`));
            data.push([rectangles[i].name]);
        }
        const interval = updateInterval;

        const timer = performance.now();
        const totalFrameCount = (endFrame - startFrame) / interval;
        console.log(`Processing ${Math.round(totalFrameCount)} frames...`);

        for (let frame = startFrame; frame <= endFrame; frame += interval) {
            video.currentTime = frame;
            await new Promise((resolve) => video.addEventListener("seeked", resolve, { once: true }));

            timestamps.push(frame);

            let jobs = [];
            for (let i = 0; i < rectangles.length; i++) {
                paintPreview(rectangles[i], canvasList[i]);
                jobs.push(scheduler.addJob("recognize", canvasList[i].toDataURL()));
            }
            const jobResults = await Promise.all(jobs);
            for (let i = 0; i < rectangles.length; i++) {
                const text = jobResults[i].data.text.trim();
                data[i].push(text);

                // Try to extract a numeric value for charting
                const num = parseFloat(text.replace(/,/g, '')); // handle "1,234"
                numericData[i].push(isNaN(num) ? null : num);
            }
            setResults(data);
            setProgress(((frame - startFrame) / (endFrame - startFrame)) * 100);
        }
        setProgress(100);
        console.log("Data printed below");
        console.log(data);

        // Store for the graph
        setTimestamps([...timestamps]);
        setChartData(numericData);

        // Build CSV with timestamp as the first column, then one column per region
        const header = ["timestamp", ...rectangles.map(r => r.name)];
        let csvRows = [header];

        for (let i = 0; i < timestamps.length; i++) {
            const row = [timestamps[i].toFixed(3)];
            for (let r = 0; r < rectangles.length; r++) {
                row.push(data[r][i + 1]); // +1 because data[r][0] is the region name
            }
            csvRows.push(row);
        }

        // Simple CSV escaping (reusing the spirit of arrayToCsv)
        const csvData = csvRows
            .map(row =>
                row
                    .map(v => String(v).replaceAll('"', '""'))
                    .map(v => `"${v}"`)
                    .join(",")
            )
            .join("\r\n");

        let filename = videoFile.name.split(".")[0] + "_results.csv";
        setBlobData(csvData);
        downloadBlob(csvData, filename, "text/csv");
        const runtime = (performance.now() - timer) / 1000;
        console.log(`Processing complete. Total runtime: ${runtime.toFixed(2)} seconds`);
        setRuntime(runtime);
        setIsProcessing(false);

        // Default the graph to show the first region (and second if available)
        const defaultSelection = [];
        if (rectangles.length > 0) defaultSelection.push(0);
        if (rectangles.length > 1) defaultSelection.push(1);
        setSelectedRegionsForGraph(defaultSelection);

        setProcessingComplete(true);
    };

    const saveCSV = () => {
        let filename = videoFile.name.split(".")[0] + "_results.csv";
        downloadBlob(blobData, filename, "text/csv");
    };

    // Toggle region selection for the graph (max 2)
    const toggleRegionForGraph = (index) => {
        setSelectedRegionsForGraph(prev => {
            if (prev.includes(index)) {
                return prev.filter(i => i !== index);
            } else if (prev.length < 2) {
                return [...prev, index];
            }
            return prev; // max 2
        });
    };

    // Prepare data for the chart
    const prepareChartData = () => {
        if (timestamps.length === 0 || selectedRegionsForGraph.length === 0) {
            return null;
        }

        const labels = timestamps.map(t => t.toFixed(2));

        const datasets = selectedRegionsForGraph.map((regionIndex, i) => {
            const regionName = rectangles[regionIndex]?.name || `Region ${regionIndex}`;
            const data = chartData[regionIndex] || [];

            return {
                label: regionName,
                data: data,
                borderColor: i === 0 ? '#3b82f6' : '#f59e0b',
                backgroundColor: i === 0 ? 'rgba(59, 130, 246, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                yAxisID: i === 0 ? 'y' : 'y1',
                tension: 0.1,
                pointRadius: 1.5,
            };
        });

        return {
            labels,
            datasets,
        };
    };

    const chartDataForDisplay = prepareChartData();

    // When hovering the chart, seek the video and update the hover preview canvases
    useEffect(() => {
        if (hoveredTimestampIndex === null || timestamps.length === 0 || selectedRegionsForGraph.length === 0) {
            return;
        }

        const targetTime = timestamps[hoveredTimestampIndex];
        const video = document.getElementById("video");
        if (!video) return;

        video.currentTime = targetTime;

        const onSeeked = () => {
            selectedRegionsForGraph.forEach((regionIndex) => {
                const targetCanvas = document.getElementById(`hover-preview-${regionIndex}`);
                if (targetCanvas) {
                    paintPreview(rectangles[regionIndex], targetCanvas);
                }
            });
        };

        video.addEventListener("seeked", onSeeked, { once: true });

        return () => {
            video.removeEventListener("seeked", onSeeked);
        };
    }, [hoveredTimestampIndex, selectedRegionsForGraph, timestamps, rectangles]);

    const mmmCoffee = () => {
        const links = [
            "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
            "https://www.youtube.com/watch?v=0q6yphdZhUA",
            "https://www.youtube.com/watch?v=K9PrE044sLg",
        ];
        const randomLink = links[Math.floor(Math.random() * links.length)];
        window.open(randomLink, "_blank");
        setTrolled(true);
    };

    const paintPreview = (rectangle, previewCanvas) => {
        const video = document.getElementById("video");
        const canvas = document.getElementById("canvas");
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(rectangle.x, rectangle.y, rectangle.width, rectangle.height);

        // Invert image data if checkbox is checked
        if (rectangle.invertImage) {
            invertImageFilter(imageData);
        }

        // Apply threshold filter
        let thresholdValue = rectangle.thresholdValue;
        thresholdFilter(imageData, thresholdValue);

        previewCanvas.width = rectangle.width;
        previewCanvas.height = rectangle.height;
        previewCanvas.getContext("2d", { willReadFrequently: true }).putImageData(imageData, 0, 0);
        return imageData;
    };

    // === Single return with conditional sections ===
    // This ensures the video, canvas, and preview canvases ALWAYS exist in the DOM
    // before we try to grab them with getElementById inside processVideoInternal.
    return (
        <Box className={"center-box"} sx={{ maxWidth: '100%' }}>
            {!hasStarted ? (
                // === Review screen ===
                <>
                    <Typography variant="h6" gutterBottom>Review &amp; Extract</Typography>

                    <Paper variant="outlined" sx={{ p: 3, mb: 3, textAlign: "left" }}>
                        <Typography variant="subtitle1" gutterBottom>Extraction Summary</Typography>
                        <Stack spacing={1}>
                            <Typography>Video: <strong>{videoFile?.name}</strong></Typography>
                            <Typography>Time range: <strong>{startFrame?.toFixed(2)}s — {endFrame?.toFixed(2)}s</strong> ({(endFrame - startFrame).toFixed(1)}s)</Typography>
                            <Typography>Regions defined: <strong>{rectangles.length}</strong></Typography>
                            <Typography>Sample rate: <strong>{(1 / updateInterval).toFixed(1)} fps</strong> ({updateInterval.toFixed(3)}s interval)</Typography>
                            <Typography>Total frames to process: <strong>{totalFrames}</strong></Typography>
                            <Typography>OCR Engine: <strong>{OCR_ENGINE_LABELS[ocrEngineType] || ocrEngineType}</strong></Typography>
                        </Stack>
                    </Paper>

                    <Button
                        variant="contained"
                        size="large"
                        onClick={startExtraction}
                        disabled={rectangles.length === 0}
                    >
                        Start Extraction
                    </Button>

                    {rectangles.length === 0 && (
                        <Typography color="error" sx={{ mt: 2 }}>
                            Add at least one region in the previous step before starting.
                        </Typography>
                    )}

                    <Box sx={{ mt: 4, opacity: 0.7 }}>
                        <Typography variant="body2">
                            Processing happens 100% in your browser using Tesseract.js. No data is sent anywhere.
                        </Typography>
                    </Box>
                </>
            ) : (
                // === Processing / Results screen ===
                <>
                    {!processingComplete && (
                        <>
                            <Typography variant="h6">Processing Video</Typography>
                            <LinearProgress variant="determinate" value={progress} />
                        </>
                    )}

                    {/* Old centered buttons removed - now in left column */}

                    {!processingComplete && (
                        <Typography variant="body1">
                            Progress: {progress.toFixed(2)}% — Frame ~{Math.floor((progress / 100) * totalFrames)} / {totalFrames}
                        </Typography>
                    )}

                    {/* Live preview table — shown only while actively processing */}
                    {!processingComplete && (
                        <TableContainer component={Paper} sx={{ mt: 2 }}>
                            <Table sx={{ minWidth: 650 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Region</TableCell>
                                        <TableCell>Preview</TableCell>
                                        <TableCell>Latest Data</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rectangles.map((rectangle, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{rectangle.name}</TableCell>
                                            <TableCell>
                                                <canvas id={`preview${index}`} key={index} width={rectangle.width} height={rectangle.height} />
                                            </TableCell>
                                            <TableCell>{results && results[index] ? results[index][results[index].length - 1] : "N/A"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {/* Post-processing two-column layout */}
                    {processingComplete && (
                        <Box sx={{ display: 'flex', gap: 3, mt: 2, width: '100%' }}>
                            
                            {/* Left narrow column */}
                            <Box sx={{ width: 260, flexShrink: 0 }}>
                                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                    <Stack spacing={1.5}>
                                        <Button 
                                            disabled={blobData === null} 
                                            variant="contained" 
                                            color="primary" 
                                            fullWidth
                                            onClick={saveCSV}
                                        >
                                            Download CSV Again
                                        </Button>
                                        <Button 
                                            variant="contained" 
                                            color="secondary" 
                                            fullWidth
                                            onClick={mmmCoffee}
                                        >
                                            Buy me a coffee!
                                        </Button>
                                    </Stack>

                                    {trolled && (
                                        <Typography 
                                            variant="body2" 
                                            color="success.main" 
                                            sx={{ mt: 1.5, fontStyle: 'italic', fontSize: '0.8rem' }}
                                        >
                                            Thanks for clicking but it's all run locally so it is very cheap. Happy Analyzing!
                                        </Typography>
                                    )}
                                </Paper>

                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>Processing Stats</Typography>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        Runtime: <strong>{runtime.toFixed(2)}s</strong>
                                    </Typography>
                                    <Typography variant="body2">
                                        Speed: <strong>{totalFrames > 0 ? (totalFrames / runtime).toFixed(1) : 0} fps</strong>
                                    </Typography>
                                </Paper>

                                {/* Hover preview images - shown when hovering the graph */}
                                {hoveredTimestampIndex !== null && selectedRegionsForGraph.length > 0 && (
                                    <Paper variant="outlined" sx={{ p: 1.5, mt: 2 }}>
                                        <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                            Preview at {timestamps[hoveredTimestampIndex]?.toFixed(2)}s
                                        </Typography>
                                        {selectedRegionsForGraph.map((regionIndex) => (
                                            <Box key={regionIndex} sx={{ mb: 1 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {rectangles[regionIndex]?.name || `Region ${regionIndex}`}
                                                </Typography>
                                                <canvas
                                                    id={`hover-preview-${regionIndex}`}
                                                    width={rectangles[regionIndex].width}
                                                    height={rectangles[regionIndex].height}
                                                    style={{
                                                        border: '1px solid #ccc',
                                                        display: 'block',
                                                        maxWidth: '100%',
                                                        imageRendering: 'pixelated',
                                                    }}
                                                />
                                            </Box>
                                        ))}
                                    </Paper>
                                )}
                            </Box>

                            {/* Right large column - chart area */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                {/* Region selector */}
                                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Select up to 2 regions to plot
                                    </Typography>
                                    <Stack direction="row" spacing={2} flexWrap="wrap">
                                        {rectangles.map((rect, index) => (
                                            <FormControlLabel
                                                key={index}
                                                control={
                                                    <Checkbox
                                                        checked={selectedRegionsForGraph.includes(index)}
                                                        onChange={() => toggleRegionForGraph(index)}
                                                        disabled={selectedRegionsForGraph.length >= 2 && !selectedRegionsForGraph.includes(index)}
                                                    />
                                                }
                                                label={rect.name || `Region ${index}`}
                                            />
                                        ))}
                                    </Stack>
                                </Paper>

                                {/* Very large chart */}
                                <Paper variant="outlined" sx={{ p: 2, height: 'calc(100vh - 260px)', minHeight: 520 }}>
                                    {chartDataForDisplay ? (
                                        <Line
                                            data={chartDataForDisplay}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                interaction: {
                                                    mode: 'index',
                                                    intersect: false,
                                                },
                                                onHover: (event, elements) => {
                                                    if (elements.length > 0) {
                                                        const index = elements[0].index;
                                                        if (index !== hoveredTimestampIndex) {
                                                            setHoveredTimestampIndex(index);
                                                        }
                                                    } else if (hoveredTimestampIndex !== null) {
                                                        setHoveredTimestampIndex(null);
                                                    }
                                                },
                                                scales: {
                                                    x: {
                                                        title: {
                                                            display: true,
                                                            text: 'Time (seconds)',
                                                        },
                                                    },
                                                    y: {
                                                        type: 'linear',
                                                        display: true,
                                                        position: 'left',
                                                        title: {
                                                            display: true,
                                                            text: rectangles[selectedRegionsForGraph[0]]?.name || 'Value',
                                                        },
                                                    },
                                                    y1: selectedRegionsForGraph.length > 1 ? {
                                                        type: 'linear',
                                                        display: true,
                                                        position: 'right',
                                                        grid: {
                                                            drawOnChartArea: false,
                                                        },
                                                        title: {
                                                            display: true,
                                                            text: rectangles[selectedRegionsForGraph[1]]?.name || 'Value',
                                                        },
                                                    } : undefined,
                                                },
                                                plugins: {
                                                    legend: {
                                                        position: 'top',
                                                    },
                                                    title: {
                                                        display: true,
                                                        text: 'Telemetry Over Time',
                                                    },
                                                },
                                            }}
                                        />
                                    ) : (
                                        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Typography color="text.secondary">
                                                Select 1 or 2 regions above to display the graph
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>
                            </Box>
                        </Box>
                    )}
                </>
            )}

            {/* Source video + full-size canvas used for cropping + filtering.
                Hidden from the user. */}
            <Box sx={{ display: 'none' }}>
                <video id="video" ref={videoRef} src={videoSrc} muted />
                <canvas ref={canvasRef} id="canvas"></canvas>
            </Box>

            {/* Always-rendered preview canvases (one per region) so they exist in the DOM
                before processVideoInternal runs. This prevents the "null width" race condition. */}
            <Box sx={{ display: 'none' }}>
                {rectangles.map((rect, index) => (
                    <canvas 
                        key={index} 
                        id={`preview${index}`} 
                        width={rect.width} 
                        height={rect.height} 
                    />
                ))}
            </Box>
        </Box>
    );
}

export default ProcessVideo;
